import React, { useState, useEffect, useCallback } from "react";
import {
  FaPray,
  FaRupeeSign,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaSearch,
  FaSync,
  FaCheck,
  FaTimes,
  FaEye,
  FaTrash,
  FaFileExcel,
  FaCalendarAlt,
  FaEnvelope,
  FaPhoneAlt,
  FaWhatsapp,
  FaCopy,
  FaOm,
  FaReceipt,
} from "react-icons/fa";
import { API_ENDPOINTS } from "../../config/api";
import SathyanarayanaReceipt from "../../components/SathyanarayanaReceipt";
import "../styles/ManagePoojaBookings.css";

const PRESET_REJECTION_REASONS = [
  "UTR / Transaction reference does not match temple bank statements.",
  "Payment amount received does not match the registered seva dakshina.",
  "Payment receipt screenshot is blurry, missing, or unreadable.",
  "Duplicate transaction reference submitted.",
  "Payment was reversed or cancelled by issuing bank.",
];

const ManagePoojaBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [stats, setStats] = useState({
    totalCount: 0,
    pendingCount: 0,
    approvedCount: 0,
    rejectedCount: 0,
    totalRevenue: 0,
  });

  // Action modals
  const [inspectBooking, setInspectBooking] = useState(null);
  const [rejectingBooking, setRejectingBooking] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [receiptBooking, setReceiptBooking] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [previewScreenshot, setPreviewScreenshot] = useState(null);
  const [toastMsg, setToastMsg] = useState("");
  const [copiedUtr, setCopiedUtr] = useState(null);

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(""), 4000);
  };

  const handleCopyUtr = (utr) => {
    navigator.clipboard.writeText(utr);
    setCopiedUtr(utr);
    setTimeout(() => setCopiedUtr(null), 2000);
  };

  /* ================= FETCH BOOKINGS ================= */
  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      const url = `${API_ENDPOINTS.POOJA_BOOKINGS}?status=${statusFilter}&search=${encodeURIComponent(search)}`;
      const res = await fetch(url);
      const data = await res.json();

      if (res.ok && data.success) {
        setBookings(data.bookings || []);
        if (data.stats) {
          setStats(data.stats);
        }
      } else {
        showToast(data.message || "Failed to load bookings");
      }
    } catch (err) {
      console.error("Error fetching bookings:", err);
      showToast("Error connecting to server");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  /* ================= APPROVE BOOKING ================= */
  const handleApprove = async (booking) => {
    const confirmApprove = window.confirm(
      `Approve payment & confirm booking for devotee "${booking.devoteeName}" (${booking.poojaName})?\n\nAn official confirmation email will be sent to: ${booking.email}`
    );
    if (!confirmApprove) return;

    try {
      setActionLoading(true);
      const res = await fetch(API_ENDPOINTS.POOJA_BOOKINGS_APPROVE(booking._id), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();

      if (res.ok && data.success) {
        showToast(`✅ Payment approved for ${booking.devoteeName}! Confirmation email sent.`);
        fetchBookings();
      } else {
        showToast(data.message || "Failed to approve booking.");
      }
    } catch (err) {
      showToast("Error processing approval.");
    } finally {
      setActionLoading(false);
    }
  };

  /* ================= REJECT BOOKING ================= */
  const openRejectModal = (booking) => {
    setRejectingBooking(booking);
    setRejectionReason(PRESET_REJECTION_REASONS[0]);
  };

  const handleConfirmReject = async () => {
    if (!rejectingBooking) return;
    if (!rejectionReason.trim()) {
      alert("Please provide a reason for rejecting this payment.");
      return;
    }

    try {
      setActionLoading(true);
      const res = await fetch(API_ENDPOINTS.POOJA_BOOKINGS_REJECT(rejectingBooking._id), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rejectionReason }),
      });
      const data = await res.json();

      if (res.ok && data.success) {
        showToast(`⚠️ Booking ${rejectingBooking.bookingId} rejected. Rejection email sent with explanation.`);
        setRejectingBooking(null);
        setRejectionReason("");
        fetchBookings();
      } else {
        showToast(data.message || "Failed to reject booking.");
      }
    } catch (err) {
      showToast("Error processing rejection.");
    } finally {
      setActionLoading(false);
    }
  };

  /* ================= DELETE BOOKING ================= */
  const handleDelete = async (id, bookingToken) => {
    if (!window.confirm(`Permanently delete booking record ${bookingToken}?`)) return;

    try {
      const res = await fetch(`${API_ENDPOINTS.POOJA_BOOKINGS}/${id}`, {
        method: "DELETE",
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showToast(`Booking ${bookingToken} deleted.`);
        fetchBookings();
      } else {
        showToast(data.message || "Failed to delete.");
      }
    } catch (err) {
      showToast("Error deleting booking record.");
    }
  };

  /* ================= EXPORT CSV ================= */
  const handleExportCsv = () => {
    if (bookings.length === 0) {
      alert("No bookings available to export.");
      return;
    }

    const headers = [
      "Booking ID",
      "Devotee Name",
      "Email",
      "Phone Number",
      "WhatsApp Number",
      "Pooja Name",
      "Preferred Date",
      "Amount (INR)",
      "Transaction ID (UTR)",
      "Payment Status",
      "Rejection Reason",
      "Gothram",
      "Rasi / Star",
      "City / Address",
      "Sankalpam Wishes",
      "Booking Created At",
    ];

    const rows = bookings.map((b) => [
      `"${b.bookingId || ""}"`,
      `"${b.devoteeName || ""}"`,
      `"${b.email || ""}"`,
      `"${b.phoneNumber || ""}"`,
      `"${b.whatsappNumber || ""}"`,
      `"${b.poojaName || ""}"`,
      `"${b.poojaDate || ""}"`,
      b.amount || 0,
      `"${b.transactionId || ""}"`,
      `"${b.paymentStatus || "Pending"}"`,
      `"${(b.rejectionReason || "").replace(/"/g, '""')}"`,
      `"${b.gothram || ""}"`,
      `"${b.rasi || ""}"`,
      `"${(b.address || "").replace(/"/g, '""')}"`,
      `"${(b.sankalpamWishes || "").replace(/"/g, '""')}"`,
      `"${new Date(b.createdAt).toLocaleString("en-IN")}"`,
    ]);

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Sri_Gnana_Sai_Pooja_Bookings_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="manage-pooja-bookings-container">
      {/* TOAST NOTIFICATION */}
      {toastMsg && <div className="admin-floating-toast">{toastMsg}</div>}

      {/* BANNER HEADER */}
      <div className="admin-pooja-banner">
        <div className="banner-info">
          <span className="banner-tag">
            <FaOm /> MANDIR SEVA ADMINISTRATION
          </span>
          <h1>🪔 Manage Pooja Bookings & Payments</h1>
          <p>
            Verify devotee Seva Dakshina payments, inspect transaction screenshots & UTRs, and approve or reject pooja bookings. Automated notification emails are triggered on registration and status updates.
          </p>
        </div>

        <div className="banner-actions">
          <button
            type="button"
            className="admin-export-btn"
            onClick={handleExportCsv}
            title="Download Excel / CSV"
          >
            <FaFileExcel />
            <span>Export to Excel</span>
          </button>

          <button
            type="button"
            className="admin-refresh-btn"
            onClick={fetchBookings}
            title="Refresh bookings"
          >
            <FaSync />
          </button>
        </div>
      </div>

      {/* METRIC STATS ROW */}
      <div className="admin-pooja-stats-grid">
        <div className="stat-box all">
          <div className="stat-icon-wrap">
            <FaPray />
          </div>
          <div className="stat-content">
            <h3>{stats.totalCount || bookings.length}</h3>
            <p>Total Bookings</p>
          </div>
        </div>

        <div className="stat-box pending">
          <div className="stat-icon-wrap">
            <FaClock />
          </div>
          <div className="stat-content">
            <h3>{stats.pendingCount || 0}</h3>
            <p>Pending Verification</p>
          </div>
        </div>

        <div className="stat-box approved">
          <div className="stat-icon-wrap">
            <FaCheckCircle />
          </div>
          <div className="stat-content">
            <h3>{stats.approvedCount || 0}</h3>
            <p>Approved & Blessed</p>
          </div>
        </div>

        <div className="stat-box revenue">
          <div className="stat-icon-wrap">
            <FaRupeeSign />
          </div>
          <div className="stat-content">
            <h3>₹{Number(stats.totalRevenue || 0).toLocaleString("en-IN")}</h3>
            <p>Verified Dakshina</p>
          </div>
        </div>
      </div>

      {/* CONTROLS BAR: STATUS TABS & SEARCH */}
      <div className="admin-controls-card">
        <div className="status-tabs-row">
          {["All", "Pending", "Approved", "Rejected"].map((st) => (
            <button
              key={st}
              type="button"
              className={`status-tab-btn ${statusFilter === st ? "active" : ""} ${st.toLowerCase()}`}
              onClick={() => setStatusFilter(st)}
            >
              {st === "All" && `All Bookings (${stats.totalCount || bookings.length})`}
              {st === "Pending" && `Pending (${stats.pendingCount || 0})`}
              {st === "Approved" && `Approved (${stats.approvedCount || 0})`}
              {st === "Rejected" && `Rejected (${stats.rejectedCount || 0})`}
            </button>
          ))}
        </div>

        <div className="admin-search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by devotee name, email, phone, UTR, or pooja..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              className="clear-search-btn"
              onClick={() => setSearch("")}
            >
              <FaTimes />
            </button>
          )}
        </div>
      </div>

      {/* BOOKINGS TABLE */}
      <div className="admin-table-wrapper">
        {loading ? (
          <div className="admin-table-loading">
            <div className="admin-spinner" />
            <p>Loading temple pooja bookings...</p>
          </div>
        ) : bookings.length === 0 ? (
          <div className="admin-table-empty">
            <FaPray className="empty-icon" />
            <h3>No Pooja Bookings Found</h3>
            <p>No devotee bookings match the selected status or search filter.</p>
          </div>
        ) : (
          <table className="admin-pooja-table">
            <thead>
              <tr>
                <th>Booking Token</th>
                <th>Devotee Details</th>
                <th>Pooja & Date</th>
                <th>Dakshina</th>
                <th>Payment UTR</th>
                <th>Receipt Screenshot</th>
                <th>Status</th>
                <th>Booked On</th>
                <th>Verification Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((booking) => (
                <tr key={booking._id}>
                  {/* TOKEN */}
                  <td>
                    <span className="token-badge">{booking.bookingId}</span>
                  </td>

                  {/* DEVOTEE DETAILS */}
                  <td>
                    <div className="devotee-cell">
                      <strong className="devotee-name">{booking.devoteeName}</strong>
                      <div className="devotee-meta">
                        <span><FaEnvelope /> {booking.email}</span>
                        <span><FaPhoneAlt /> {booking.phoneNumber}</span>
                        {booking.whatsappNumber && booking.whatsappNumber !== booking.phoneNumber && (
                          <span style={{ color: "#15803d" }}><FaWhatsapp /> {booking.whatsappNumber}</span>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* POOJA & DATE */}
                  <td>
                    <div className="pooja-cell">
                      <span className="pooja-name-tag">{booking.poojaName}</span>
                      <small className="pooja-date-text">
                        <FaCalendarAlt /> {booking.poojaDate}
                      </small>
                    </div>
                  </td>

                  {/* AMOUNT */}
                  <td>
                    <strong className="dakshina-amount">
                      ₹{Number(booking.amount || 501).toLocaleString("en-IN")}
                    </strong>
                  </td>

                  {/* UTR */}
                  <td>
                    <div className="utr-cell">
                      <code className="utr-code">{booking.transactionId || "N/A"}</code>
                      {booking.transactionId && (
                        <button
                          type="button"
                          className="mini-copy-btn"
                          onClick={() => handleCopyUtr(booking.transactionId)}
                          title="Copy UTR"
                        >
                          {copiedUtr === booking.transactionId ? <FaCheck style={{ color: "#15803d" }} /> : <FaCopy />}
                        </button>
                      )}
                    </div>
                  </td>

                  {/* SCREENSHOT */}
                  <td>
                    {booking.paymentScreenshot ? (
                      <div
                        className="screenshot-thumb-wrapper"
                        onClick={() => setPreviewScreenshot(booking.paymentScreenshot)}
                        title="Click to zoom receipt"
                      >
                        <img
                          src={booking.paymentScreenshot}
                          alt="Receipt"
                          className="screenshot-thumb-img"
                        />
                      </div>
                    ) : (
                      <span className="no-screenshot-tag">No Image</span>
                    )}
                  </td>

                  {/* STATUS */}
                  <td>
                    <span
                      className={`status-pill ${
                        booking.paymentStatus?.toLowerCase() === "approved"
                          ? "approved"
                          : booking.paymentStatus?.toLowerCase() === "rejected"
                          ? "rejected"
                          : "pending"
                      }`}
                    >
                      {booking.paymentStatus || "Pending"}
                    </span>
                    {booking.paymentStatus === "Rejected" && booking.rejectionReason && (
                      <small className="rejection-hint-text" title={booking.rejectionReason}>
                        Reason: {booking.rejectionReason.slice(0, 30)}...
                      </small>
                    )}
                  </td>

                  {/* DATE */}
                  <td>
                    <span className="date-text">{formatDate(booking.createdAt)}</span>
                  </td>

                  {/* ACTIONS */}
                  <td>
                    <div className="action-buttons-cell">
                      {/* APPROVE BUTTON (TICK ICON WITH PASTEL GLASSMORPHISM) */}
                      {booking.paymentStatus !== "Approved" && (
                        <button
                          type="button"
                          className="btn-action-approve"
                          onClick={() => handleApprove(booking)}
                          disabled={actionLoading}
                          title="Approve Payment & Send Confirmation Email"
                          aria-label="Approve Payment"
                        >
                          <FaCheck />
                        </button>
                      )}

                      {/* REJECT BUTTON (WRONG ICON WITH PASTEL GLASSMORPHISM) */}
                      {booking.paymentStatus !== "Rejected" && (
                        <button
                          type="button"
                          className="btn-action-reject"
                          onClick={() => openRejectModal(booking)}
                          disabled={actionLoading}
                          title="Reject Payment & Send Notification Email"
                          aria-label="Reject Payment"
                        >
                          <FaTimes />
                        </button>
                      )}

                      {/* OFFICIAL MANDIR RECEIPT FOR ALL POOJAS */}
                      <button
                        type="button"
                        className="btn-action-receipt"
                        onClick={() => setReceiptBooking(booking)}
                        title={`View & Print Official Mandir Receipt (${booking.poojaName})`}
                      >
                        <FaReceipt />
                        <span>Receipt</span>
                      </button>

                      {/* INSPECT DETAILS */}
                      <button
                        type="button"
                        className="btn-action-inspect"
                        onClick={() => setInspectBooking(booking)}
                        title="Inspect Full Devotee Sankalpam Details"
                      >
                        <FaEye />
                      </button>

                      {/* DELETE */}
                      <button
                        type="button"
                        className="btn-action-delete"
                        onClick={() => handleDelete(booking._id, booking.bookingId)}
                        title="Delete record"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ================= REJECTION REASON MODAL ================= */}
      {rejectingBooking && (
        <div className="admin-modal-overlay" onClick={() => setRejectingBooking(null)}>
          <div className="admin-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header reject-header">
              <h3>
                <FaTimesCircle /> Reject Payment & Notify Devotee
              </h3>
              <button
                type="button"
                className="admin-modal-close"
                onClick={() => setRejectingBooking(null)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="admin-modal-body">
              <div className="reject-devotee-summary">
                <p>
                  <strong>Devotee:</strong> {rejectingBooking.devoteeName} ({rejectingBooking.email})
                </p>
                <p>
                  <strong>Pooja:</strong> {rejectingBooking.poojaName} | <strong>Dakshina:</strong> ₹{rejectingBooking.amount}
                </p>
                <p>
                  <strong>Submitted UTR:</strong> <code>{rejectingBooking.transactionId}</code>
                </p>
              </div>

              <div className="rejection-form-group">
                <label>Select Preset Reason or Enter Custom Reason:</label>
                <div className="preset-reasons-list">
                  {PRESET_REJECTION_REASONS.map((preset, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={`preset-reason-btn ${rejectionReason === preset ? "selected" : ""}`}
                      onClick={() => setRejectionReason(preset)}
                    >
                      {preset}
                    </button>
                  ))}
                </div>

                <label style={{ marginTop: "12px" }}>Custom Rejection Reason (Included in Devotee Email):</label>
                <textarea
                  rows="3"
                  className="rejection-textarea"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Explain why the payment could not be approved..."
                  required
                />
                <small className="rejection-notice-hint">
                  📧 An automated email will immediately be sent to <strong>{rejectingBooking.email}</strong> informing them of the rejection and detailing this exact reason along with mandir helpline contact numbers.
                </small>
              </div>
            </div>

            <div className="admin-modal-footer">
              <button
                type="button"
                className="admin-cancel-btn"
                onClick={() => setRejectingBooking(null)}
                disabled={actionLoading}
              >
                Cancel
              </button>

              <button
                type="button"
                className="admin-confirm-reject-btn"
                onClick={handleConfirmReject}
                disabled={actionLoading}
              >
                {actionLoading ? "Sending Notification..." : "Confirm Rejection & Send Email"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= INSPECT FULL DETAILS MODAL ================= */}
      {inspectBooking && (
        <div className="admin-modal-overlay" onClick={() => setInspectBooking(null)}>
          <div className="admin-modal-card inspect-card" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>
                <FaPray /> Devotee Sankalpam Details [{inspectBooking.bookingId}]
              </h3>
              <button
                type="button"
                className="admin-modal-close"
                onClick={() => setInspectBooking(null)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="admin-modal-body inspect-grid">
              <div className="inspect-item">
                <span className="inspect-lbl">Devotee Full Name:</span>
                <strong>{inspectBooking.devoteeName}</strong>
              </div>
              <div className="inspect-item">
                <span className="inspect-lbl">Pooja Ritual:</span>
                <strong style={{ color: "#0f2d5c" }}>{inspectBooking.poojaName}</strong>
              </div>
              <div className="inspect-item">
                <span className="inspect-lbl">Email Address:</span>
                <span>{inspectBooking.email}</span>
              </div>
              <div className="inspect-item">
                <span className="inspect-lbl">Mobile Number:</span>
                <span>{inspectBooking.phoneNumber}</span>
              </div>
              <div className="inspect-item">
                <span className="inspect-lbl">WhatsApp Number:</span>
                <span>{inspectBooking.whatsappNumber || inspectBooking.phoneNumber}</span>
              </div>
              <div className="inspect-item">
                <span className="inspect-lbl">Preferred Pooja Date:</span>
                <strong>{inspectBooking.poojaDate}</strong>
              </div>
              <div className="inspect-item">
                <span className="inspect-lbl">Gothram:</span>
                <span>{inspectBooking.gothram || "—"}</span>
              </div>
              <div className="inspect-item">
                <span className="inspect-lbl">Rasi / Nakshatra:</span>
                <span>{inspectBooking.rasi || "—"} {inspectBooking.nakshatra ? `(${inspectBooking.nakshatra})` : ""}</span>
              </div>
              <div className="inspect-item">
                <span className="inspect-lbl">Devotee City / Address:</span>
                <span>{inspectBooking.address || "—"}</span>
              </div>
              <div className="inspect-item">
                <span className="inspect-lbl">Seva Dakshina Amount:</span>
                <strong style={{ color: "#059669" }}>₹{inspectBooking.amount}</strong>
              </div>
              <div className="inspect-item">
                <span className="inspect-lbl">Transaction ID / UTR:</span>
                <code>{inspectBooking.transactionId}</code>
              </div>
              <div className="inspect-item">
                <span className="inspect-lbl">Payment Status:</span>
                <span className={`status-pill ${inspectBooking.paymentStatus?.toLowerCase()}`}>
                  {inspectBooking.paymentStatus}
                </span>
              </div>

              {inspectBooking.rejectionReason && (
                <div className="inspect-item full-width" style={{ background: "#fff1f2", padding: "10px", borderRadius: "8px" }}>
                  <span className="inspect-lbl" style={{ color: "#9f1239" }}>Rejection Reason:</span>
                  <p style={{ margin: "4px 0 0 0", color: "#881337", fontWeight: "600" }}>{inspectBooking.rejectionReason}</p>
                </div>
              )}

              {inspectBooking.sankalpamWishes && (
                <div className="inspect-item full-width">
                  <span className="inspect-lbl">Special Prayer Wishes / Sankalpam:</span>
                  <p className="sankalpam-wishes-text">{inspectBooking.sankalpamWishes}</p>
                </div>
              )}

              {inspectBooking.paymentScreenshot && (
                <div className="inspect-item full-width">
                  <span className="inspect-lbl">Payment Screenshot:</span>
                  <div className="inspect-screenshot-wrap">
                    <img
                      src={inspectBooking.paymentScreenshot}
                      alt="Receipt"
                      onClick={() => setPreviewScreenshot(inspectBooking.paymentScreenshot)}
                      title="Click to view full image"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="admin-modal-footer">
              <button
                type="button"
                className="admin-print-receipt-btn"
                onClick={() => setReceiptBooking(inspectBooking)}
              >
                <FaReceipt /> Print Official Mandir Receipt
              </button>
              <button
                type="button"
                className="admin-cancel-btn"
                onClick={() => setInspectBooking(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= SCREENSHOT ZOOM MODAL ================= */}
      {previewScreenshot && (
        <div className="admin-modal-overlay" onClick={() => setPreviewScreenshot(null)}>
          <div className="admin-zoom-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="admin-zoom-close"
              onClick={() => setPreviewScreenshot(null)}
            >
              <FaTimes />
            </button>
            <img src={previewScreenshot} alt="Full Receipt" className="admin-zoom-img" />
          </div>
        </div>
      )}

      {/* ================= OFFICIAL SATHYANARAYANA RECEIPT (FOR SATHYANARAYANA POOJA ONLY) ================= */}
      {receiptBooking && (
        <SathyanarayanaReceipt
          booking={receiptBooking}
          isOpen={!!receiptBooking}
          onClose={() => setReceiptBooking(null)}
        />
      )}
    </div>
  );
};

export default ManagePoojaBookings;
